/**
 * useAnalytics Hook
 *
 * Type-safe analytics tracking hook that integrates with Mixpanel.
 * Provides methods for tracking funnel events, engagement, and drop-offs.
 *
 * @example
 * const { trackFunnel, trackEngagement, trackDropOff } = useAnalytics();
 *
 * // Track funnel event
 * trackFunnel(FUNNEL_EVENTS.WORKSHOP_STARTED, { has_existing_session: false });
 *
 * // Track engagement
 * trackEngagement(ENGAGEMENT_EVENTS.CTA_CLICKED, { cta_name: 'hero_cta', cta_location: 'landing' });
 *
 * @see https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript
 * @version 1.0.0 - November 2025
 */

import {useCallback, useMemo} from 'react';
import {logger} from '@/shared/lib/logger/logger';

import type {
    DropOffEventName,
    DropOffEventPropertyMap,
    EngagementEventName,
    EngagementEventPropertyMap,
    FunnelEventName,
    FunnelEventPropertyMap,
} from '@/shared/analytics';
import {validateEventProperties} from '@/shared/analytics/schemas';
import {env} from '@/shared/config/environment';
import {startTiming as mixpanelStartTiming, trackEvent} from '@/shared/utils/mixpanel';
import {getCurrentSessionId} from '@/shared/utils/session';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Device type detection result.
 */
type DeviceType = 'mobile' | 'tablet' | 'desktop';

/**
 * Enriched properties added automatically to every event.
 */
interface EnrichedProperties {
  timestamp: string;
  page_url: string;
  session_id: string | null;
  device_type: DeviceType;
  locale: string;
  referrer: string;
}

/**
 * Return type of useAnalytics hook.
 */
interface UseAnalyticsReturn {
  /**
   * Track a funnel event with type-safe properties.
   */
  trackFunnel: <E extends FunnelEventName>(
    event: E,
    properties: E extends keyof FunnelEventPropertyMap
      ? FunnelEventPropertyMap[E]
      : Record<string, unknown>
  ) => void;

  /**
   * Track an engagement event with type-safe properties.
   */
  trackEngagement: <E extends EngagementEventName>(
    event: E,
    properties: E extends keyof EngagementEventPropertyMap
      ? EngagementEventPropertyMap[E]
      : Record<string, unknown>
  ) => void;

  /**
   * Track a drop-off event with type-safe properties.
   */
  trackDropOff: <E extends DropOffEventName>(
    event: E,
    properties: E extends keyof DropOffEventPropertyMap
      ? DropOffEventPropertyMap[E]
      : Record<string, unknown>
  ) => void;

  /**
   * Start timing an event. Call before the action starts.
   * The duration will be automatically calculated when the event is tracked.
   */
  startTiming: (eventName: string) => void;

  /**
   * Check if analytics is enabled.
   */
  isEnabled: boolean;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Detect device type based on screen width.
 */
function getDeviceType(): DeviceType {
  if (typeof window === 'undefined') return 'desktop';

  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

/**
 * Get enriched properties that are added to every event.
 */
function getEnrichedProperties(): EnrichedProperties {
  return {
    timestamp: new Date().toISOString(),
    page_url: typeof window !== 'undefined' ? window.location.href : '',
    session_id: typeof window !== 'undefined' ? getCurrentSessionId() : null,
    device_type: getDeviceType(),
    locale:
      typeof navigator !== 'undefined' ? navigator.language : 'en',
    referrer: typeof document !== 'undefined' ? document.referrer : '',
  };
}

/**
 * Check if analytics is enabled based on environment config.
 */
function isAnalyticsEnabled(): boolean {
  return !!env.mixpanel.enabled;
}

/**
 * Log event to console in development mode.
 */
function logEventInDev(
  eventType: 'funnel' | 'engagement' | 'drop-off',
  eventName: string,
  properties: Record<string, unknown>
): void {
  if (env.environment === 'development') {
    logger.debug(`[Analytics] ${eventType.toUpperCase()}: ${eventName}`, {
      component: 'useAnalytics',
      action: 'track_event',
      eventType,
      eventName,
      ...properties,
    });
  }
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook for type-safe analytics tracking.
 *
 * Features:
 * - Type-safe event tracking with property validation
 * - Automatic enrichment with session, device, and locale info
 * - Development logging
 * - Integration with Mixpanel
 *
 * @returns Analytics tracking methods
 */
export function useAnalytics(): UseAnalyticsReturn {
  const isEnabled = useMemo(() => isAnalyticsEnabled(), []);

  /**
   * Track a funnel event.
   */
  const trackFunnel = useCallback(
    <E extends FunnelEventName>(
      event: E,
      properties: E extends keyof FunnelEventPropertyMap
        ? FunnelEventPropertyMap[E]
        : Record<string, unknown>
    ): void => {
      const enrichedProps: Record<string, unknown> = {
        ...getEnrichedProperties(),
        ...properties,
        event_category: 'funnel',
      };

      // Validate event properties (non-blocking)
      const validatedProps = validateEventProperties(
        event,
        enrichedProps,
        'funnel'
      );

      logEventInDev('funnel', event, validatedProps);

      if (isEnabled) {
        trackEvent(event, validatedProps);
      }
    },
    [isEnabled]
  );

  /**
   * Track an engagement event.
   */
  const trackEngagement = useCallback(
    <E extends EngagementEventName>(
      event: E,
      properties: E extends keyof EngagementEventPropertyMap
        ? EngagementEventPropertyMap[E]
        : Record<string, unknown>
    ): void => {
      const enrichedProps: Record<string, unknown> = {
        ...getEnrichedProperties(),
        ...properties,
        event_category: 'engagement',
      };

      // Validate event properties (non-blocking)
      const validatedProps = validateEventProperties(
        event,
        enrichedProps,
        'engagement'
      );

      logEventInDev('engagement', event, validatedProps);

      if (isEnabled) {
        trackEvent(event, validatedProps);
      }
    },
    [isEnabled]
  );

  /**
   * Track a drop-off event.
   */
  const trackDropOff = useCallback(
    <E extends DropOffEventName>(
      event: E,
      properties: E extends keyof DropOffEventPropertyMap
        ? DropOffEventPropertyMap[E]
        : Record<string, unknown>
    ): void => {
      const enrichedProps: Record<string, unknown> = {
        ...getEnrichedProperties(),
        ...properties,
        event_category: 'drop_off',
      };

      // Validate event properties (non-blocking)
      const validatedProps = validateEventProperties(
        event,
        enrichedProps,
        'drop-off'
      );

      logEventInDev('drop-off', event, validatedProps);

      if (isEnabled) {
        trackEvent(event, validatedProps);
      }
    },
    [isEnabled]
  );

  /**
   * Start timing an event.
   */
  const startTiming = useCallback(
    (eventName: string): void => {
      if (isEnabled) {
        mixpanelStartTiming(eventName);
      }
    },
    [isEnabled]
  );

  return useMemo(
    () => ({
      trackFunnel,
      trackEngagement,
      trackDropOff,
      startTiming,
      isEnabled,
    }),
    [trackFunnel, trackEngagement, trackDropOff, startTiming, isEnabled]
  );
}

// =============================================================================
// STANDALONE FUNCTIONS (for use outside React components)
// =============================================================================

/**
 * Track a funnel event outside of React components.
 * Use useAnalytics hook inside components when possible.
 */
export function trackFunnelEvent<E extends FunnelEventName>(
  event: E,
  properties: E extends keyof FunnelEventPropertyMap
    ? FunnelEventPropertyMap[E]
    : Record<string, unknown>
): void {
  const enrichedProps: Record<string, unknown> = {
    ...getEnrichedProperties(),
    ...properties,
    event_category: 'funnel',
  };

  // Validate event properties (non-blocking)
  const validatedProps = validateEventProperties(event, enrichedProps, 'funnel');

  logEventInDev('funnel', event, validatedProps);

  if (isAnalyticsEnabled()) {
    trackEvent(event, validatedProps);
  }
}
